import { useEffect, useState } from "react";
import { X, Percent, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { adminApi } from "@/lib/api";
import type { AdminTeamSummary, TeamBillingConfig } from "@/lib/types";
import { useLanguage } from "@/contexts/LanguageContext";

function ModalShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full mx-4 sci-card p-6 max-h-[90vh] overflow-y-auto max-w-[480px]">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-base font-semibold text-white" style={{ fontFamily: "Space Grotesk, sans-serif" }}>
            {title}
          </h2>
          <button onClick={onClose} className="text-slate-500 hover:text-white">
            <X size={16} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// previewDiscount renders a live "× 10 = X 折 / Y%" style preview for a raw
// discount-rate input string while the admin is still typing, independent of
// the stricter (0,1] submit-time validation below. Out-of-range/unparsable
// input just renders "—" instead of an error, so it doesn't fight the field's
// own validation message.
function previewDiscount(raw: string, language: "zh" | "en"): string {
  const n = Number(raw.trim());
  if (!Number.isFinite(n) || n <= 0 || n > 1) return "—";
  const percent = Math.round(n * 100);
  if (language !== "zh") return `${percent}%`;
  const zhe = Math.round(n * 100) / 10;
  const zheStr = Number.isInteger(zhe) ? String(zhe) : zhe.toFixed(1);
  return `${zheStr} 折 / ${percent}%`;
}

// BillingConfigModal edits a team's discount rate + billing cycle (Task 6).
// It replaces the earlier read-only summary that lived inline in
// AdminTeams.tsx (FE Task 4) — that version only ever fetched and displayed
// TeamBillingConfig; this one prefills the same fetch into editable fields
// and submits via adminApi.updateBillingConfig.
export default function BillingConfigModal({
  team,
  onClose,
  onSaved,
}: {
  team: AdminTeamSummary;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t, language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [discountRate, setDiscountRate] = useState("");
  const [billingCycle, setBillingCycle] = useState<"weekly" | "monthly">("monthly");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    adminApi
      .getBillingConfig(team.team.id)
      .then((cfg: TeamBillingConfig) => {
        setDiscountRate(cfg.discount_rate);
        setBillingCycle(cfg.billing_cycle);
      })
      .catch((err: any) => {
        toast.error(err?.response?.data?.message || "Failed to load billing config");
      })
      .finally(() => setLoading(false));
  }, [team.team.id]);

  const submit = async () => {
    const raw = discountRate.trim();
    if (raw === "") {
      toast.error(t("billing.discountLabel") + " " + t("admin.common.required"));
      return;
    }
    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0 || n > 1) {
      toast.error(t("billing.discountRateInvalid"));
      return;
    }
    setBusy(true);
    try {
      await adminApi.updateBillingConfig(team.team.id, {
        discount_rate: raw,
        billing_cycle: billingCycle,
      });
      toast.success(t("billing.configUpdatedToast"));
      onSaved();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell
      title={`${t("admin.teams.modalBillingConfigTitle")} — ${team.team.name}`}
      onClose={onClose}
    >
      {loading ? (
        <div className="text-xs text-slate-500 text-center py-6">Loading…</div>
      ) : (
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-slate-400 mb-1.5 flex items-center gap-1">
              <Percent size={10} /> {t("billing.discountLabel")}
            </Label>
            <Input
              type="number"
              min="0"
              max="1"
              step="any"
              value={discountRate}
              onChange={(e) => setDiscountRate(e.target.value)}
              placeholder="0.9"
              className="bg-white/5 border-white/10 text-white text-sm"
              autoFocus
            />
            <p className="text-[10px] text-slate-500 mt-1.5">
              {t("billing.discountRateHint")}
              {" — "}
              <span className="text-slate-300 font-mono">
                {previewDiscount(discountRate, language)}
              </span>
            </p>
          </div>
          <div>
            <Label className="text-xs text-slate-400 mb-1.5 flex items-center gap-1">
              <CalendarClock size={10} /> {t("billing.cycleLabel")}
            </Label>
            <Select
              value={billingCycle}
              onValueChange={(v) => setBillingCycle(v as "weekly" | "monthly")}
            >
              <SelectTrigger className="w-full bg-white/5 border-white/10 text-white text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly">{t("billing.cycleMonthly")}</SelectItem>
                <SelectItem value="weekly">{t("billing.cycleWeekly")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <p className="text-[10px] text-slate-500 pt-2 border-t border-white/5">
            {t("admin.teams.billingConfigHint")}
          </p>
        </div>
      )}
      <div className="flex gap-3 pt-5">
        <Button variant="ghost" onClick={onClose} className="flex-1 text-slate-400 hover:text-white border border-white/10">
          {t("admin.common.cancel")}
        </Button>
        <Button onClick={submit} disabled={busy || loading} className="flex-1 btn-primary-sci">
          {busy ? "..." : t("admin.common.update")}
        </Button>
      </div>
    </ModalShell>
  );
}
